import React, { useEffect, useRef, useState } from "react";
import { Music, VolumeX } from "lucide-react";

/**
 * Universal background music player.
 * Supports:
 *  - Direct audio URLs (mp3/wav/ogg) — HTML5 <audio>
 *  - YouTube URLs — YouTube IFrame API
 *  - Spotify URLs — falls back to a small "Buka di Spotify" pill (no autoplay allowed)
 *
 * Auto-plays on mount (best-effort — some browsers block until user gesture,
 * so a floating play/pause button always visible in corner).
 *
 * Props:
 *  - url: string
 *  - startSeconds: number (defaults 0)
 *  - color: accent color for the toggle
 *  - bg: background color for the toggle chip
 *  - testid: root data-testid
 */
function parseYouTube(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname.startsWith("/watch")) return u.searchParams.get("v");
      if (u.pathname.startsWith("/embed/")) return u.pathname.split("/")[2];
      if (u.pathname.startsWith("/shorts/")) return u.pathname.split("/")[2];
    }
  } catch { /* invalid URL */ }
  return null;
}

function loadYouTubeAPI() {
  return new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === "function") try { prev(); } catch {}
      resolve(window.YT);
    };
    if (!document.getElementById("yt-iframe-api")) {
      const tag = document.createElement("script");
      tag.id = "yt-iframe-api"; tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);
    }
  });
}

export default function MusicPlayer({ url, startSeconds = 0, color = "#c9a35c", bg = "rgba(0,0,0,0.5)", testid = "music-player" }) {
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const audioRef = useRef(null);
  const ytPlayerRef = useRef(null);
  const ytContainerId = useRef(`yt-bg-${Math.random().toString(36).slice(2)}`);

  const ytId = parseYouTube(url);
  const isSpotify = url && /spotify\.com|open\.spotify/i.test(url);
  const isAudio = url && !ytId && !isSpotify;
  const start = Math.max(0, parseInt(startSeconds, 10) || 0);

  // HTML5 audio path
  useEffect(() => {
    if (!isAudio) return;
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = start;
    a.loop = true;
    a.volume = 0.5;
    const tryPlay = async () => {
      try { await a.play(); setPlaying(true); }
      catch { setPlaying(false); }
    };
    tryPlay();
    return () => { try { a.pause(); } catch {} };
  }, [isAudio, url, start]);

  // YouTube path
  useEffect(() => {
    if (!ytId) return;
    let destroyed = false;
    (async () => {
      const YT = await loadYouTubeAPI();
      if (destroyed) return;
      // Ensure host element exists
      if (!document.getElementById(ytContainerId.current)) return;
      ytPlayerRef.current = new YT.Player(ytContainerId.current, {
        videoId: ytId,
        width: "0", height: "0",
        playerVars: { autoplay: 1, loop: 1, playlist: ytId, start, controls: 0, playsinline: 1, modestbranding: 1 },
        events: {
          onReady: (e) => {
            setReady(true);
            try {
              e.target.seekTo(start, true);
              e.target.setVolume(50);
              e.target.playVideo();
            } catch {}
          },
          onStateChange: (e) => {
            if (!e || !e.data) return;
            if (e.data === YT.PlayerState.PLAYING) setPlaying(true);
            if (e.data === YT.PlayerState.PAUSED || e.data === YT.PlayerState.ENDED) setPlaying(false);
            if (e.data === YT.PlayerState.ENDED) {
              try { e.target.seekTo(start, true); e.target.playVideo(); } catch {}
            }
          },
        },
      });
    })();
    return () => {
      destroyed = true;
      try { ytPlayerRef.current?.destroy(); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ytId]);

  const toggle = () => {
    if (isAudio && audioRef.current) {
      const a = audioRef.current;
      if (a.paused) { a.currentTime = a.currentTime || start; a.play().then(() => setPlaying(true)).catch(() => {}); }
      else { a.pause(); setPlaying(false); }
      return;
    }
    if (ytId && ytPlayerRef.current) {
      try {
        const state = ytPlayerRef.current.getPlayerState?.();
        if (state === 1) { ytPlayerRef.current.pauseVideo(); setPlaying(false); }
        else { ytPlayerRef.current.playVideo(); setPlaying(true); }
      } catch {}
      return;
    }
    if (isSpotify) window.open(url, "_blank");
  };

  if (!url) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-40" data-testid={testid}>
      {/* Media host (hidden) */}
      {isAudio && <audio ref={audioRef} src={url} loop preload="auto" />}
      {ytId && (
        <div style={{ position: "absolute", left: "-9999px", top: "-9999px", width: 0, height: 0, overflow: "hidden" }}>
          <div id={ytContainerId.current} />
        </div>
      )}

      {/* Floating play/pause */}
      <button
        type="button"
        data-testid={`${testid}-toggle`}
        onClick={toggle}
        aria-label={playing ? "Jeda musik" : "Putar musik"}
        title={playing ? "Jeda musik" : "Putar musik"}
        className="pointer-events-auto fixed bottom-6 right-6 flex h-12 w-12 items-center justify-center rounded-full border backdrop-blur-md transition-transform hover:-translate-y-0.5"
        style={{ borderColor: color, color, background: bg, boxShadow: `0 6px 20px ${color}44` }}
      >
        {playing ? <Music className="h-5 w-5 animate-pulse" /> : <VolumeX className="h-5 w-5" />}
      </button>
    </div>
  );
}
